import { genderSchema } from '#shared/types';
import { Button } from '#client/components/ui/Button';
import { Card } from '#client/components/ui/Card';
import { NumberField } from '#client/components/ui/NumberField';
import { Select } from '#client/components/ui/Select';
import { TextArea } from '#client/components/ui/TextArea';
import { TextField } from '#client/components/ui/TextField';
import type { DraftLine, RequestFields } from './order-request.logic';
import type { SetField } from '#client/hooks/useFormValues';
import type { Repeater } from '#client/hooks/useRepeater';
import { GENDER_OPTIONS } from '#client/lib/form-choices';

interface RequestPatientCardProps {
  values: RequestFields;
  onFieldChange: SetField<RequestFields>;
  errors: Record<string, string>;
}

export function RequestPatientCard({ values, onFieldChange, errors }: RequestPatientCardProps) {
  return (
    <Card title="Who it is for" subtitle="Only the name is needed — fill in what the fax says">
      <div className="c-form-grid">
        <TextField
          label="Patient name"
          value={values.patient_name}
          error={errors.patient_name}
          onChange={(event) => onFieldChange('patient_name', event.target.value)}
          required
        />
        <TextField
          label="Contact"
          value={values.phone_number ?? ''}
          error={errors.phone_number}
          onChange={(event) => onFieldChange('phone_number', event.target.value)}
        />
        <NumberField
          label="Age"
          min={0}
          nullable
          value={values.age_year}
          error={errors.age_year}
          onChange={(years) => onFieldChange('age_year', years)}
          suffix="years"
        />
        <Select
          label="Gender"
          options={GENDER_OPTIONS}
          placeholder="Not given"
          value={values.gender ?? ''}
          error={errors.gender}
          onChange={(choice) => onFieldChange('gender', choice ? genderSchema.parse(choice) : null)}
        />
        <TextField
          label="Referred by"
          value={values.referred_by ?? ''}
          error={errors.referred_by}
          onChange={(event) => onFieldChange('referred_by', event.target.value)}
          helpText="The doctor or panel who sent it"
        />
        <TextField
          label="How it arrived"
          value={values.source ?? ''}
          error={errors.source}
          onChange={(event) => onFieldChange('source', event.target.value)}
          helpText="Fax, WhatsApp, phone, walk-in — whatever it was"
        />
      </div>

      <TextArea
        label="Note"
        rows={2}
        value={values.note ?? ''}
        error={errors.note}
        onChange={(event) => onFieldChange('note', event.target.value)}
      />
    </Card>
  );
}

interface RequestedLinesCardProps {
  lines: Repeater<DraftLine>;
  testOptions: { value: string; label: string }[];
  linesError: string | undefined;
}

export function RequestedLinesCard({ lines, testOptions, linesError }: RequestedLinesCardProps) {
  return (
    <Card
      title="What was asked for"
      subtitle="Type it exactly as written, then match it to a test if you can"
      actions={
        <Button size="sm" onClick={lines.add}>
          Add a test
        </Button>
      }
    >
      {linesError && <p className="c-import-missing">{linesError}</p>}

      <ul className="c-order-lines">
        {lines.rows.map((line, index) => (
          <li key={lines.keys[index]} className="c-order-line">
            <TextField
              label="As written"
              value={line.requested_text}
              onChange={(event) => lines.update(index, { requested_text: event.target.value })}
              helpText='"LFT", "CBC + ESR" — whatever the fax says'
              required
            />
            <Select
              label="Which test"
              options={testOptions}
              // Not required. A fax saying "LFT" is a legitimate request
              // whether or not anybody has matched it yet, and forcing the
              // match here would mean guessing at the counter.
              placeholder="Match it later"
              value={line.test_id === null ? '' : String(line.test_id)}
              onChange={(choice) =>
                lines.update(index, { test_id: choice ? Number(choice) : null })
              }
            />
            {lines.rows.length > 1 && (
              <Button variant="ghost" size="sm" onClick={() => lines.remove(index)}>
                Remove
              </Button>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}
